import { useResponsive } from './useResponsive';

/**
 * Specialized hook for handling drawer responsiveness
 * Centralizes dynamic width logic and responsive props
 *
 * @param {number} defaultWidth - Default width for desktop (default: 448)
 */
export const useResponsiveDrawer = (defaultWidth = 448) => {
  const { shouldUseMobileLayout, isMobile, screenWidth } = useResponsive();

  // Calculate drawer width based on screen size
  const getDrawerWidth = () => {
    if (isMobile) {
      return '100%'; // On mobile, use full width
    }

    if (screenWidth < 1180) {
      return Math.min(480, screenWidth * 0.8); // On tablet, use 80% of width max 480px
    }

    return defaultWidth; // On desktop, use provided width (default: 448)
  };

  // Get responsive classes for drawer
  const getDrawerClasses = (customClass) => {
    const baseClasses = ['border-bottom-none'];

    if (shouldUseMobileLayout) {
      baseClasses.push('isMobileOnly');
    }

    if (customClass) {
      baseClasses.push(customClass);
    }

    return baseClasses.join(' ');
  };

  // Get responsive props for Space component
  const getSpaceProps = () => ({
    size: isMobile ? 8 : 16,
    direction: isMobile ? 'vertical' : 'horizontal',
    style: {
      width: isMobile ? '100%' : 'auto',
      marginTop: isMobile ? '16px' : '0',
    },
  });

  // Get responsive props for Button component
  const getButtonProps = () => ({
    size: isMobile ? 'large' : 'middle',
    style: {
      width: isMobile ? '100%' : 'auto',
      minWidth: isMobile ? 'auto' : '80px',
    },
  });

  /** Primary drawer width in px; null on mobile where width is 100%. Used for paired drawer `calc(100vw - px)`. */
  const getPrimaryWidthPx = () => {
    if (isMobile) return null;
    const w = getDrawerWidth();
    return typeof w === 'number' ? w : defaultWidth;
  };

  return {
    getDrawerWidth,
    getDrawerClasses,
    getSpaceProps,
    getButtonProps,
    getPrimaryWidthPx,
    isMobile,
    shouldUseMobileLayout,
  };
};
